import { format, parseISO } from "date-fns"
import type { AccountingCheckJson } from "@/api/accounting.tsx"

/**
 * Heuristic for physical check vs card/ACH/wire. Used for **sorting** (check-like first), not filtering.
 * Unknown / empty (e.g. Xero) is treated as check-like so we do not bury those rows.
 */
const NON_CHECK_PAYMENT_RE =
  /\b(ach|a\.?\s*r\.?\s*ach|automated clearing|wire(\s*transfer)?|credit\s*card|debit\s*card|visa|mastercard|\bmc\b|amex|american express|discover|paypal|venmo|zelle|eft\b|electronic funds|direct deposit|online(\s+transfer)?|epayment|e-payment|bank(\s+transfer)?)\b/i

export function is_check_like_payment(payment_method: string | undefined): boolean {
  const m = payment_method?.trim()
  if (!m) {
    return true
  }
  if (NON_CHECK_PAYMENT_RE.test(m)) {
    return false
  }
  if (/^\s*cash\s*$/i.test(m)) {
    return false
  }
  return true
}

/** 1 = check-like payment method, 0 = clearly non-check — for descending sort (checks first). */
export function score_check_type_for_sort(payment_method: string | undefined): number {
  return is_check_like_payment(payment_method) ? 1 : 0
}

function escape_regex_chars(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Sort priority for accounting payments whose customer best matches the deduction source (e.g. UNFI vs UNFI East/West).
 * Higher = better match. Returns 0 when we cannot score (unknown customer or generic source).
 */
export function score_customer_for_deduction_source(
  customer_name: string | undefined,
  deduction_source: string | undefined
): number {
  if (!deduction_source?.trim() || !customer_name?.trim()) {
    return 0
  }

  const src_raw = deduction_source.trim().toLowerCase()
  const src_compact = src_raw.replace(/[^a-z0-9]/g, "")
  if (src_compact.length < 2) {
    return 0
  }
  // Too noisy to boost rows for generic buckets
  if (src_compact === "other") {
    return 0
  }

  const cust_compact = customer_name.toLowerCase().replace(/[^a-z0-9]/g, "")

  if (src_compact.length >= 3 && cust_compact.includes(src_compact)) {
    return 3
  }

  const token = src_raw.replace(/[^a-z0-9\s]/g, "").trim()
  if (token.length >= 2) {
    try {
      const re = new RegExp(`\\b${escape_regex_chars(token)}\\b`, "i")
      if (re.test(customer_name)) {
        return 3
      }
    } catch {
      // ignore invalid patterns
    }
  }

  return 0
}

/** Strips a leading "Check #"/"check" label and whitespace so "check 2000258938" and "2000258938" compare equal. */
export function normalize_check_number_key(raw: string): string {
  let s = raw.trim().toLowerCase()
  s = s.replace(/^check\s*#?\s*/i, "")
  s = s.replace(/\s+/g, "")
  return s
}

export function split_check_number_row_display(raw: string): { show_check_label: boolean; digits: string } {
  const trimmed = raw.trim()
  const m = trimmed.match(/^check\s*#?\s*(.+)$/i)
  if (m) {
    return { show_check_label: true, digits: m[1].trim() }
  }
  return { show_check_label: false, digits: trimmed }
}

function dollars_equal(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined) return false
  return Math.round(a * 100) === Math.round(b * 100)
}

function dates_equal_calendar(a?: string, b?: string): boolean {
  if (!a || !b) return false
  try {
    return format(parseISO(a), "yyyy-MM-dd") === format(parseISO(b), "yyyy-MM-dd")
  } catch {
    return false
  }
}

export interface AccountingCheckMatchHints {
  checkNumber: string
  amountDollars?: number
  checkDateIso?: string
  /** Deduction `Source` for this check page (e.g. `unfi`) — used to rank QuickBooks customers like UNFI East/West. */
  deductionSource?: string
}

/** Picks the best accounting check that matches the internal check number; tie-breaks with amount, date, and existing link. */
export function find_best_accounting_check_match(
  hints: AccountingCheckMatchHints,
  checks: AccountingCheckJson[],
  internal_check_id?: string
): AccountingCheckJson | null {
  const hint_key = normalize_check_number_key(hints.checkNumber)
  if (!hint_key) return null

  const candidates = checks.filter((ac) => {
    if (ac.linked_check_id && ac.linked_check_id !== internal_check_id) {
      return false
    }
    return normalize_check_number_key(ac.check_number) === hint_key
  })
  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0]!

  const sorted = [...candidates].sort((a, b) => {
    const a_linked = internal_check_id && a.linked_check_id === internal_check_id
    const b_linked = internal_check_id && b.linked_check_id === internal_check_id
    if (a_linked !== b_linked) {
      return a_linked ? -1 : 1
    }
    const a_amt = dollars_equal(hints.amountDollars, a.total_amount)
    const b_amt = dollars_equal(hints.amountDollars, b.total_amount)
    if (a_amt !== b_amt) {
      return a_amt ? -1 : 1
    }
    const a_date = dates_equal_calendar(hints.checkDateIso, a.check_date)
    const b_date = dates_equal_calendar(hints.checkDateIso, b.check_date)
    if (a_date !== b_date) {
      return a_date ? -1 : 1
    }

    const a_src = score_customer_for_deduction_source(a.customer_name, hints.deductionSource)
    const b_src = score_customer_for_deduction_source(b.customer_name, hints.deductionSource)
    if (a_src !== b_src) {
      return b_src - a_src
    }

    const a_chk = score_check_type_for_sort(a.payment_method)
    const b_chk = score_check_type_for_sort(b.payment_method)
    if (a_chk !== b_chk) {
      return b_chk - a_chk
    }

    const ad = a.check_date ?? ""
    const bd = b.check_date ?? ""
    return bd.localeCompare(ad)
  })
  return sorted[0] ?? null
}

/**
 * Sort for the dropdown: check-like payment types first, then customer vs deduction source, matching
 * check number, unlinked, newest date.
 */
export function sort_accounting_checks_for_picker(
  checks: AccountingCheckJson[],
  hints: AccountingCheckMatchHints | undefined,
  internal_check_id: string | undefined
): AccountingCheckJson[] {
  const hint_key = hints?.checkNumber?.trim() ? normalize_check_number_key(hints.checkNumber) : ""
  return [...checks].sort((a, b) => {
    const a_type = score_check_type_for_sort(a.payment_method)
    const b_type = score_check_type_for_sort(b.payment_method)
    if (a_type !== b_type) {
      return b_type - a_type
    }
    if (hints?.deductionSource?.trim()) {
      const as = score_customer_for_deduction_source(a.customer_name, hints.deductionSource)
      const bs = score_customer_for_deduction_source(b.customer_name, hints.deductionSource)
      if (as !== bs) {
        return bs - as
      }
    }
    if (hint_key) {
      const a_match = normalize_check_number_key(a.check_number) === hint_key ? 1 : 0
      const b_match = normalize_check_number_key(b.check_number) === hint_key ? 1 : 0
      if (a_match !== b_match) {
        return b_match - a_match
      }
    }
    if (a.linked_check_id && !b.linked_check_id) return 1
    if (!a.linked_check_id && b.linked_check_id) return -1
    const ad = a.check_date ?? ""
    const bd = b.check_date ?? ""
    return bd.localeCompare(ad)
  })
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("normalize_check_number_key", () => {
    it("strips check prefix and spacing", () => {
      expect(normalize_check_number_key("check 2000258938")).toBe("2000258938")
      expect(normalize_check_number_key("CHECK\t2000258938")).toBe("2000258938")
      expect(normalize_check_number_key("Check #2000258938")).toBe("2000258938")
    })
    it("leaves plain numbers", () => {
      expect(normalize_check_number_key("603684")).toBe("603684")
    })
  })

  describe("find_best_accounting_check_match", () => {
    it("returns null when no number match", () => {
      const checks: AccountingCheckJson[] = [
        { id: "1", check_number: "999", total_amount: 100 },
      ]
      expect(
        find_best_accounting_check_match({ checkNumber: "888" }, checks, undefined)
      ).toBeNull()
    })
    it("picks the row matching amount when two share a check number", () => {
      const checks: AccountingCheckJson[] = [
        { id: "a", check_number: "100", total_amount: 50 },
        { id: "b", check_number: "100", total_amount: 99 },
      ]
      const best = find_best_accounting_check_match({ checkNumber: "100", amountDollars: 99 }, checks, undefined)
      expect(best?.id).toBe("b")
    })
    it("skips rows linked to another internal check", () => {
      const checks: AccountingCheckJson[] = [
        { id: "a", check_number: "100", total_amount: 99, linked_check_id: "other-check" },
        { id: "b", check_number: "200", total_amount: 99 },
      ]
      expect(find_best_accounting_check_match({ checkNumber: "100" }, checks, "mine")).toBeNull()
    })
    it("allows the row already linked to this check", () => {
      const checks: AccountingCheckJson[] = [
        { id: "a", check_number: "100", total_amount: 50, linked_check_id: "mine" },
      ]
      expect(find_best_accounting_check_match({ checkNumber: "100" }, checks, "mine")?.id).toBe("a")
    })
    it("prefers the customer that matches deduction source when numbers collide", () => {
      const checks: AccountingCheckJson[] = [
        { id: "east", check_number: "100", total_amount: 99, customer_name: "UNFI East" },
        { id: "west", check_number: "100", total_amount: 99, customer_name: "Acme Grocery" },
      ]
      const best = find_best_accounting_check_match(
        { checkNumber: "100", amountDollars: 99, deductionSource: "unfi" },
        checks,
        undefined
      )
      expect(best?.id).toBe("east")
    })
    it("prefers check payment over ACH when both match number and customer", () => {
      const checks: AccountingCheckJson[] = [
        { id: "ach", check_number: "100", total_amount: 99, customer_name: "UNFI East", payment_method: "ACH" },
        {
          id: "chk",
          check_number: "100",
          total_amount: 99,
          customer_name: "UNFI East",
          payment_method: "Check",
        },
      ]
      const best = find_best_accounting_check_match(
        { checkNumber: "100", amountDollars: 99, deductionSource: "unfi" },
        checks,
        undefined
      )
      expect(best?.id).toBe("chk")
    })
  })

  describe("sort_accounting_checks_for_picker", () => {
    it("orders check-like payments before ACH for the same customer", () => {
      const checks: AccountingCheckJson[] = [
        { id: "ach", check_number: "1", total_amount: 1, customer_name: "UNFI East", payment_method: "ACH" },
        { id: "chk", check_number: "2", total_amount: 1, customer_name: "UNFI East", payment_method: "Check" },
      ]
      const sorted = sort_accounting_checks_for_picker(checks, { checkNumber: "x" }, undefined)
      expect(sorted.map((c) => c.id)).toEqual(["chk", "ach"])
    })
  })

  describe("is_check_like_payment", () => {
    it("treats checks and unknown labels as check-like for sorting", () => {
      expect(is_check_like_payment(undefined)).toBe(true)
      expect(is_check_like_payment("Check")).toBe(true)
      expect(is_check_like_payment("UNFI West - Check")).toBe(true)
    })
    it("classifies common non-check methods", () => {
      expect(is_check_like_payment("ACH")).toBe(false)
      expect(is_check_like_payment("Wire Transfer")).toBe(false)
      expect(is_check_like_payment("Credit Card")).toBe(false)
      expect(is_check_like_payment("Cash")).toBe(false)
    })
  })

  describe("score_customer_for_deduction_source", () => {
    it("matches UNFI regional customer names for source unfi", () => {
      expect(score_customer_for_deduction_source("UNFI East", "unfi")).toBe(3)
      expect(score_customer_for_deduction_source("UNFI West", "unfi")).toBe(3)
      expect(score_customer_for_deduction_source("Some Retailer", "unfi")).toBe(0)
    })
  })
}
